import { FEATURES } from "@/shared/config";
import type { PlanTier } from "@/shared/types/account";

const PADDLE_SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";
const CHECKOUT_SUCCESS_QUERY = "checkout=success";
// PK-253: the email an anonymous buyer paid with, carried to /signup so the
// account they create matches the Paddle customer the subscription sits on.
const CHECKOUT_EMAIL_KEY = "checkout_email";

type PaddleEvent = {
  name: string;
  data?: {
    customer?: {
      email?: string;
    };
  };
};

type PaddleInstance = {
  Environment: {
    set: (environment: "sandbox" | "production") => void;
  };
  Initialize: (options: {
    token: string;
    checkout?: {
      settings?: {
        displayMode?: "overlay" | "inline";
        theme?: "light" | "dark";
        locale?: string;
      };
    };
    eventCallback?: (event: PaddleEvent) => void;
  }) => void;
  Checkout: {
    open: (options: {
      items: Array<{
        priceId: string;
        quantity: number;
      }>;
      customer?: {
        email?: string;
      };
      customData?: Record<string, string>;
      settings?: {
        displayMode?: "overlay";
        variant?: "one-page" | "multi-page";
        successUrl?: string;
        allowLogout?: boolean;
      };
    }) => void;
  };
};

declare global {
  interface Window {
    Paddle?: PaddleInstance;
  }
}

let paddleLoadPromise: Promise<PaddleInstance> | null = null;
let paddleInitialized = false;

function getPaddleEnvironment(): "sandbox" | "live" {
  return import.meta.env.VITE_PADDLE_ENV === "sandbox" ? "sandbox" : "live";
}

function getPaddleClientToken(): string | undefined {
  return import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
}

function getPlanPriceId(plan: PlanTier): string | undefined {
  return plan === "pro"
    ? import.meta.env.VITE_PADDLE_PRICE_ID_PRO
    : import.meta.env.VITE_PADDLE_PRICE_ID_STARTER;
}

// Account holders go back to the dashboard. Anonymous buyers (the preview
// page's priced door) have no account to land in, so they go to signup, which
// prefills the email they paid with; the WorkOS user.created webhook then
// claims the subscription by that email.
function getCheckoutSuccessUrl(anonymous: boolean): string {
  return anonymous
    ? `${window.location.origin}/signup?${CHECKOUT_SUCCESS_QUERY}`
    : `${window.location.origin}/dashboard?${CHECKOUT_SUCCESS_QUERY}`;
}

function rememberCheckoutEmail(event: PaddleEvent): void {
  if (event.name !== "checkout.completed") return;
  const email = event.data?.customer?.email;
  if (!email) return;
  try {
    window.sessionStorage.setItem(CHECKOUT_EMAIL_KEY, email);
  } catch {
    // Signup still works without the prefill.
  }
}

// Reads and clears the email stored at checkout completion.
export function takeCheckoutEmail(): string | undefined {
  try {
    const email = window.sessionStorage.getItem(CHECKOUT_EMAIL_KEY);
    window.sessionStorage.removeItem(CHECKOUT_EMAIL_KEY);
    return email ?? undefined;
  } catch {
    return undefined;
  }
}

function assertPaddleConfig(): {
  clientToken: string;
  environment: "sandbox" | "live";
} {
  const clientToken = getPaddleClientToken();
  const environment = getPaddleEnvironment();

  if (!clientToken) {
    throw new Error("Missing VITE_PADDLE_CLIENT_TOKEN");
  }

  return { clientToken, environment };
}

function loadPaddleScript(): Promise<PaddleInstance> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Paddle can only load in the browser"));
  }

  if (window.Paddle) {
    return Promise.resolve(window.Paddle);
  }

  if (paddleLoadPromise) {
    return paddleLoadPromise;
  }

  paddleLoadPromise = new Promise<PaddleInstance>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${PADDLE_SCRIPT_URL}"]`,
    );

    const handleLoad = () => {
      if (window.Paddle) {
        resolve(window.Paddle);
        return;
      }

      reject(new Error("Paddle.js loaded without exposing window.Paddle"));
    };

    if (existingScript) {
      if (window.Paddle) {
        resolve(window.Paddle);
        return;
      }

      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Paddle.js")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PADDLE_SCRIPT_URL;
    script.async = true;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Failed to load Paddle.js")),
      { once: true },
    );
    document.head.appendChild(script);
  });

  return paddleLoadPromise;
}

export async function initializePaddle(): Promise<void> {
  if (!FEATURES.enableBilling) {
    return;
  }

  const { clientToken, environment } = assertPaddleConfig();
  const paddle = await loadPaddleScript();

  if (paddleInitialized) {
    return;
  }

  if (environment === "sandbox") {
    paddle.Environment.set("sandbox");
  }

  paddle.Initialize({
    token: clientToken,
    checkout: {
      settings: {
        displayMode: "overlay",
        theme: "light",
        locale: "en",
      },
    },
    eventCallback: rememberCheckoutEmail,
  });

  paddleInitialized = true;
}

export async function openUpgradeCheckout(options: {
  plan: PlanTier;
  email?: string | null;
  userId?: string | null;
  // PK-253: set by the priced door so a conversion traces back to the
  // proposal whose download it followed.
  proposalId?: string | null;
  source?: string;
}): Promise<void> {
  const priceId = getPlanPriceId(options.plan);
  if (!priceId) {
    throw new Error(`Missing Paddle price ID for the ${options.plan} plan`);
  }
  await initializePaddle();

  const paddle = window.Paddle;
  if (!paddle) {
    throw new Error("Paddle is not available");
  }

  const customData: Record<string, string> = {};
  if (options.userId) customData.userId = options.userId;
  if (options.proposalId) customData.proposalId = options.proposalId;
  if (options.source) customData.source = options.source;

  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: options.email
      ? {
          email: options.email,
        }
      : undefined,
    customData: Object.keys(customData).length > 0 ? customData : undefined,
    settings: {
      displayMode: "overlay",
      variant: "one-page",
      successUrl: getCheckoutSuccessUrl(!options.userId),
      allowLogout: false,
    },
  });
}

export function hasCheckoutSuccessParam(search: string): boolean {
  return new URLSearchParams(search).get("checkout") === "success";
}
